/** The record panel's PARENT PORTFOLIO COMPANY section — portco-transactions spec §2.2, slice S1.2.
 *
 * `LID - Arrow Concrete Products` is an add-on to LID Tech; `ReGenerate - Exit` is a sale process for
 * ReGenerate. Spec §2.2 requires that parentage be "surfaced and editable in the record panel so it
 * is visible team-wide, not a hidden column" — this is that surface.
 *
 * THREE THINGS THIS COMPONENT DELIBERATELY DOES NOT DO:
 *
 * 1. **It never renders `links[0]`.** A merge of two linked deals fronts TWO parents — each row
 *    perfectly unique on its own ref, both returned by the ref seam — and spec invariant 1 calls that
 *    a DATA ERROR to show, not a conflict to resolve at read time. So every link renders, and
 *    `conflict` gets its own line. Taking the first would hide it permanently and silently, and which
 *    one you saw would depend on insertion order.
 * 2. **It does not re-derive the permission.** `editable` comes from the payload (deal team, or the
 *    audited `full_deal_access` capability) and `candidates` ships empty for a read-only caller, so a
 *    UI that got the rule wrong cannot offer a control the server would refuse — and cannot hide one
 *    it would allow.
 * 3. **It does not treat "no parent" as an error.** Almost every deal on the tracker has no parent
 *    portfolio company, because almost every deal is not a transaction against one. Empty is one
 *    honest line.
 *
 * Theme tokens + CSS Modules only (`.claude/rules/frontend.md`); no hardcoded colours. */
import { useState } from 'react';

import { ApiError } from '../../api/client';
import { useClearPortcoLink, useCrmPortcoLink, useSetPortcoLink } from '../../hooks/useCrm';
import type { CrmPortcoLink as PortcoLinkRow } from '../../types';
import shell from './crm.module.css';
import styles from './CrmPortcoLink.module.css';

interface Props {
  listId: number;
  entryId: number;
  /** Lazy, like the evidence read: the section is not always open. */
  enabled?: boolean;
}

/** Human labels for `link_kind`. The vocabulary is the server's (`portco_links.LINK_KINDS`); this map
 * only spells it for a reader, and falls back to the raw value rather than dropping an unknown kind —
 * a kind this build has not heard of is information, not a rendering bug. */
const KIND_LABEL: Record<string, string> = {
  platform: 'Platform acquisition',
  add_on: 'Add-on',
  exit: 'Exit',
};

function kindLabel(kind: string): string {
  return KIND_LABEL[kind] ?? kind;
}

/** Who decided this parentage. `provenance` is load-bearing: Phase B only ever PROPOSES (spec
 * invariant 3), so `phase_b_accepted` names a HUMAN'S acceptance, never a machine's assertion. */
function provenanceLine(row: PortcoLinkRow): string {
  const parts: string[] = [];
  if (row.provenance === 'operator') parts.push('set by a colleague');
  else if (row.provenance === 'phase_b_accepted') parts.push('proposed by AI, accepted by a colleague');
  else parts.push('matched by the backfill');
  if (row.confidence) parts.push(`${row.confidence} match`);
  return parts.join(' · ');
}

export function CrmPortcoLink({ listId, entryId, enabled = true }: Props) {
  const q = useCrmPortcoLink(listId, entryId, enabled);
  const setLink = useSetPortcoLink(listId);
  const clearLink = useClearPortcoLink(listId);
  const [company, setCompany] = useState<string>('');
  const [kind, setKind] = useState<string>('add_on');

  // A 404 is "no such record, or you cannot see it" — the endpoint is existence-safe on purpose. It
  // is not a failure to shout about in a panel section.
  const notFound = q.error instanceof ApiError && q.error.status === 404;
  const failed = q.error != null && !notFound;
  const data = q.data;
  const links = data?.links ?? [];
  const editable = data?.editable === true;
  const busy = setLink.isPending || clearLink.isPending;

  // The server's own words for a refusal, shown verbatim rather than re-worded — a 403 (not on the
  // deal team) or a 400 (a company or kind the server does not recognise).
  const mutationError =
    (setLink.error instanceof ApiError && setLink.error.message) ||
    (clearLink.error instanceof ApiError && clearLink.error.message) ||
    null;

  return (
    <div className={shell.card}>
      <div className={shell.cardTitle}>Parent portfolio company</div>
      {q.isLoading && <div className={shell.loading}>Loading…</div>}
      {failed && <div className={shell.error}>Failed to load the parent portfolio company.</div>}

      {data?.conflict && (
        // ⛔ Its own line, above the rows. Two parents is not a display quirk to be smoothed over —
        // it is the state invariant 1 forbids, reached through a merge, and somebody has to fix it.
        <div className={shell.error}>
          This deal has more than one parent, which should never happen — it usually follows a merge.
          Clear the one that is wrong.
        </div>
      )}

      {!q.isLoading && !failed && links.length === 0 && (
        <div className={shell.empty}>
          Not a transaction against a portfolio company.
        </div>
      )}

      {links.map((row) => (
        <div key={row.id} className={styles.row}>
          <div className={styles.name}>{row.portfolio_company_name}</div>
          <div className={styles.meta}>
            {kindLabel(row.link_kind)} · {provenanceLine(row)}
          </div>
          {row.evidence_note && <div className={styles.note}>{row.evidence_note}</div>}
        </div>
      ))}

      {editable && (
        <div className={styles.editor}>
          <select
            aria-label="Parent portfolio company"
            className={styles.control}
            value={company}
            disabled={busy}
            onChange={(e) => setCompany(e.target.value)}
          >
            <option value="">Choose a portfolio company…</option>
            {(data?.candidates ?? []).map((c: { id: number; name: string }) => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </select>
          <select
            aria-label="Transaction kind"
            className={styles.control}
            value={kind}
            disabled={busy}
            onChange={(e) => setKind(e.target.value)}
          >
            {(data?.link_kinds ?? []).map((k: string) => (
              <option key={k} value={k}>{kindLabel(k)}</option>
            ))}
          </select>
          <button
            type="button"
            className={styles.btn}
            disabled={busy || company === ''}
            onClick={() =>
              setLink.mutate({ entryId, portfolioCompanyId: Number(company), linkKind: kind })}
          >
            Set parent
          </button>
          {links.length > 0 && (
            <button
              type="button"
              className={styles.btn}
              disabled={busy}
              onClick={() => clearLink.mutate({ entryId })}
            >
              Clear
            </button>
          )}
        </div>
      )}

      {mutationError && <div className={shell.error}>{mutationError}</div>}
    </div>
  );
}
